/**
 * The telemetry choke point.
 *
 * There is no Sentry here, and that is the point of the file. `PLAN-MOBILE.md`
 * M-01 and `WEB_REVIEW` WEB-03 both need a crash reporter, and this file
 * deliberately is not it: it is the shape underneath one — a single seam that
 * every report passes through, with redaction applied on this side of it.
 *
 * The three things that used to gate the reporter — a Sentry org, DSNs, and a
 * data-classification sign-off — all now exist (`SENTRY_DATA_POLICY.md`, in
 * force from 2026-08-05, §9 signed 2026-08-13), and `telemetry-sentry.ts`
 * installs a real client through `configureTelemetry`. This file did not have
 * to change for that, which was the whole bet.
 *
 * The ordering rule from `PLAN-OBSERVABILITY.md` is why it is built first —
 * *the scrubber is written before the exporter is enabled, not after*, because
 * a vendor's retention cannot be undone by deploying a fix. Wiring Sentry later
 * is then `configureTelemetry(sentryClient)` plus a DSN, and there is no path
 * by which a payload reaches it unredacted, because `deliver()` is the only
 * caller and it scrubs first.
 *
 * Three properties this must have, all of them tested:
 *
 *   1. **Redaction is not optional.** Callers cannot reach the client directly.
 *   2. **A telemetry failure is not an app failure.** A client that throws, or
 *      hangs, or is half-initialised must be invisible to the person using the
 *      app. `PLAN-OBSERVABILITY.md` lists this in its done-when and requires it
 *      asserted by a test rather than assumed.
 *   3. **Off by default.** With no client installed this is a no-op, so the
 *      app ships today with the seam in place and nothing leaving the device.
 *
 * ── Why this file is asserted against its source, not executed ──────────────
 *
 * It cannot be loaded by root `node --test`, and the reason is a real conflict
 * rather than an oversight: Node's ESM resolver needs an explicit `.ts`
 * extension, and TypeScript rejects one unless `allowImportingTsExtensions` is
 * set (TS5097, verified). Enabling that flag would change module resolution for
 * the whole app, and nothing else in this repository imports a `.ts` extension,
 * so it is not a pattern with a precedent here — and it cannot be validated
 * without a full Metro build.
 *
 * So `tests/telemetry/telemetry-seam.test.ts` asserts against this file's
 * source, the same way `bootstrap-wiring.spec.ts` does for `main.ts`, which is
 * also unloadable for its own reasons. What it holds is the property that
 * matters: every exported entry point redacts before it reaches `deliver()`,
 * and `deliver()` is the only thing that touches the client. The scrubber
 * itself is executed and proven in `tests/redaction/redaction.test.ts`.
 */

import { redactError, redactValue } from "@/lib/redaction";

/** Severity, kept deliberately small — these map onto every vendor. */
export type TelemetryLevel = "fatal" | "error" | "warning" | "info";

/** Arbitrary structured context. Redacted before it reaches the client. */
export type TelemetryContext = Record<string, unknown>;

export type TelemetryEvent = {
  level: TelemetryLevel;
  /** Already redacted by the time a client sees it. */
  error?: Record<string, unknown>;
  message?: string;
  context?: TelemetryContext;
};

/**
 * What a real exporter must implement.
 *
 * Deliberately not Sentry's interface: the adapter that wraps Sentry is a
 * dozen lines, and naming the vendor here would make swapping it a refactor of
 * every call site instead of one file.
 */
export type TelemetryClient = {
  send(event: TelemetryEvent): void;
  setUser?(id: string | null): void;
};

let client: TelemetryClient | null = null;

/** Installs an exporter. Called once, at startup, when a DSN exists. */
export function configureTelemetry(next: TelemetryClient | null): void {
  client = next;
}

export function isTelemetryEnabled(): boolean {
  return client !== null;
}

/**
 * Hands a redacted event to the client, and swallows anything it does.
 *
 * The swallow is the feature. A crash reporter that can turn a recoverable
 * render error into an unrecoverable one — because its own transport threw
 * inside an error handler — is worse than having none.
 */
function deliver(event: TelemetryEvent): void {
  if (!client) return;
  try {
    client.send(event);
  } catch {
    // Deliberately silent. Reporting a telemetry failure through telemetry is
    // the loop this exists to avoid, and a console warning here fires on every
    // render of a broken screen.
  }
}

/** Reports a thrown error. Everything attached is redacted first. */
export function captureException(
  error: unknown,
  context?: TelemetryContext,
  level: TelemetryLevel = "error",
): void {
  if (!client) return;

  // Named rather than inlined so the seam reads as one rule: nothing named
  // like a parameter is ever handed to deliver().
  const safeError =
    error instanceof Error
      ? redactError(error)
      : { name: "NonError", message: String(redactValue(error)) };
  const safeContext = context
    ? (redactValue(context) as TelemetryContext)
    : undefined;

  deliver({ level, error: safeError, context: safeContext });
}

/** Reports something noteworthy that did not throw. */
export function captureMessage(
  message: string,
  context?: TelemetryContext,
  level: TelemetryLevel = "info",
): void {
  if (!client) return;

  const safeMessage = String(redactValue(message));
  const safeContext = context
    ? (redactValue(context) as TelemetryContext)
    : undefined;

  deliver({ level, message: safeMessage, context: safeContext });
}

/**
 * Associates reports with an account.
 *
 * An opaque id only — never an email, handle or profile. `SECURITY_REVIEW.md`
 * denies full user profiles, and an email in a crash report is the cheapest
 * possible way to breach that.
 */
export function setTelemetryUser(id: string | null): void {
  if (!client?.setUser) return;
  try {
    client.setUser(id);
  } catch {
    // Same reasoning as deliver().
  }
}
